// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machine, in words (M3-40, NFR-025).
//
// > "A screen-reader-navigable score and status surface exists for the 2D shell."
//
// **Navigable**, which is the word that decides the design. The obvious reading — put the score
// in a live region — produces the worst possible outcome: a score that changes forty times a
// second makes a screen reader talk continuously and drowns out everything that matters,
// including the machine's own callouts, which NFR-022 already requires to be spoken. The stage's
// score is `aria-live="off"` for exactly that reason.
//
// So there are two surfaces and they answer different questions:
//
//   * **A status region that can be read on demand.** Score, ball, player, ball save, tilt — laid
//     out as a labelled list a screen-reader user can navigate into whenever they want to know,
//     and which never announces itself.
//   * **A polite line for the handful of things that are genuinely events.** A ball starting, a
//     tilt, a game ending, the machine asking for initials. Perhaps a dozen sentences in a whole
//     game, each of them a thing a sighted player learns from a sound and a flash.
//
// This file is the second one, and it is pure: given what the machine is doing now and what it
// was doing last time, what — if anything — is worth saying. Nothing here touches the DOM, so
// every one of those judgements has a test.

/** What a sentence can be built from. A subset of the HUD's readout, by design. */
export interface MachineStatus {
  readonly phase: string
  readonly ball: number
  readonly ballsPerGame: number
  readonly player: number
  readonly players: number
  readonly score: number
  readonly tilted: boolean
  readonly awaitingLaunch: boolean
  /** Which initial is being entered, or −1. */
  readonly initialsAt: number
  readonly initials: string
}

/**
 * The status surface, as label-and-value pairs.
 *
 * Read on demand rather than announced, so it can afford to be complete. Built here rather than
 * in the component so the wording is testable and so the two surfaces cannot drift apart.
 */
export function statusFields(status: MachineStatus): readonly { label: string; value: string }[] {
  const fields: { label: string; value: string }[] = [
    { label: 'Score', value: status.score.toLocaleString() },
  ]
  if (status.phase === 'attract') {
    fields.push({ label: 'Machine', value: 'in attract mode, waiting for a game' })
    return fields
  }
  fields.push({ label: 'Ball', value: `${status.ball} of ${status.ballsPerGame}` })
  if (status.players > 1) {
    fields.push({ label: 'Player', value: `${status.player} of ${status.players}` })
  }
  if (status.tilted) fields.push({ label: 'Tilt', value: 'the flippers are dead until the next ball' })
  if (status.awaitingLaunch) fields.push({ label: 'Plunger', value: 'a ball is waiting in the lane' })
  if (status.initialsAt >= 0) {
    fields.push({ label: 'Initials', value: spellOut(status.initials) })
  }
  return fields
}

/**
 * What to say out loud, or nothing.
 *
 * Called with every readout and answers `undefined` for almost all of them. The bar is a thing a
 * sighted player would learn from a flash, a sound or a whole screen changing — **not** the
 * score, which is on the status surface above and which a live region would turn into a
 * continuous stream nobody can hear past.
 */
export function narrate(now: MachineStatus, before: MachineStatus | undefined): string | undefined {
  if (before === undefined) return undefined

  // A game ending. The score belongs in *this* sentence and nowhere else in the live region: it
  // is the one moment a player wants the number read to them without asking.
  if (now.phase === 'gameOver' && before.phase !== 'gameOver') {
    return `Game over. You scored ${now.score.toLocaleString()}.`
  }

  if (now.initialsAt >= 0 && before.initialsAt < 0) {
    return `That score makes the table. Enter three initials with the flippers, and accept each one with the plunger. Letter one of three.`
  }
  // Each letter as it is accepted, because otherwise there is no way to know a press landed —
  // the only feedback a sighted player gets is the cursor moving one place to the right.
  if (now.initialsAt > before.initialsAt && now.initialsAt >= 0) {
    return `${spellOut(before.initials.slice(0, before.initialsAt + 1))}. Letter ${now.initialsAt + 1} of 3.`
  }

  if (now.tilted && !before.tilted) {
    return 'Tilt. The flippers are dead until the next ball.'
  }

  if (now.phase !== 'attract' && before.phase === 'attract') {
    return `Game started. Ball 1 of ${now.ballsPerGame}. Press the plunger to put the ball in play.`
  }

  if (now.ball !== before.ball && now.phase !== 'attract') {
    const who = now.players > 1 ? `Player ${now.player}. ` : ''
    return `${who}Ball ${now.ball} of ${now.ballsPerGame}.`
  }

  // A ball coming back to the lane mid-ball is a ball save, and it is the one thing a player can
  // be doing nothing about and needs to know about immediately.
  if (now.awaitingLaunch && !before.awaitingLaunch && now.ball === before.ball) {
    return 'Ball saved. It is back in the lane.'
  }

  return undefined
}

/**
 * Letters, one at a time, with the space said rather than left silent.
 *
 * `AAA` is read as a word by most screen readers and `A A A` is read as three letters. The
 * blank in the initials alphabet is the one that matters: unspoken it is a letter the player
 * cannot tell they have chosen.
 */
function spellOut(text: string): string {
  return [...text].map((c) => (c === ' ' ? 'space' : c === '<' ? 'rub out' : c)).join(' ')
}
